/**
 * DNCRegistryForm — Add/Edit DNC registry modal
 *
 * Modal form for creating or editing a DNCRegistry.
 * Follows the same patterns as PolicyRuleForm.tsx.
 */
import { useState, useEffect } from 'react';
import { Button, Input, Label } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import { Modal } from '@respectly/ui';
import { Loader2 } from 'lucide-react';
import type { DNCRegistry, PREChannel } from '@respectly/shared-types';
import { usePolicyStore } from '../../stores/usePolicyStore';

// ============================================
// Types
// ============================================

interface DNCRegistryFormProps {
  isOpen: boolean;
  onClose: () => void;
  registry?: DNCRegistry | null;
}

interface FormState {
  name: string;
  registryId: string;
  country: string;
  jurisdictionCode: string;
  registryType: 'NATIONAL' | 'INTERNAL';
  scrubCadenceDays: number;
  channel: PREChannel | '';
  description: string;
  isActive: boolean;
}

// ============================================
// Constants
// ============================================

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channels' },
  { value: 'VOICE', label: 'Voice' },
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
];

const REGISTRY_TYPE_OPTIONS = [
  { value: 'NATIONAL', label: 'National' },
  { value: 'INTERNAL', label: 'Internal' },
];

function getDefaultForm(): FormState {
  return {
    name: '',
    registryId: '',
    country: '',
    jurisdictionCode: '',
    registryType: 'NATIONAL',
    scrubCadenceDays: 31,
    channel: '',
    description: '',
    isActive: true,
  };
}

// ============================================
// Component
// ============================================

export function DNCRegistryForm({
  isOpen,
  onClose,
  registry,
}: DNCRegistryFormProps) {
  const createDncRegistry = usePolicyStore((s) => s.createDncRegistry);
  const updateDncRegistry = usePolicyStore((s) => s.updateDncRegistry);
  const jurisdictions = usePolicyStore((s) => s.jurisdictions);
  const isCreating = usePolicyStore((s) => s.isCreating);
  const isUpdating = usePolicyStore((s) => s.isUpdating);

  const isEdit = !!registry;
  const isSaving = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(getDefaultForm());
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    if (registry) {
      setForm({
        name: registry.name,
        registryId: registry.registryId,
        country: registry.country,
        jurisdictionCode: registry.jurisdictionCode,
        registryType: registry.registryType as 'NATIONAL' | 'INTERNAL',
        scrubCadenceDays: registry.scrubCadenceDays,
        channel: registry.channel ?? '',
        description: registry.description ?? '',
        isActive: registry.isActive,
      });
    } else {
      setForm(getDefaultForm());
    }
    setErrors({});
  }, [registry, isOpen]);

  // Auto-generate registryId for new registries
  useEffect(() => {
    if (!isEdit && form.country) {
      const base = form.country.toUpperCase().replace(/[^A-Z]/g, '');
      setForm((prev) => ({
        ...prev,
        registryId: `CUSTOM_${base}_DNC`,
      }));
    }
  }, [isEdit, form.country]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.country.trim()) newErrors.country = 'Country is required';
    if (!form.jurisdictionCode) newErrors.jurisdictionCode = 'Jurisdiction is required';
    if (form.scrubCadenceDays < 1) newErrors.scrubCadenceDays = 'Scrub cadence must be at least 1 day';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const dto = {
      name: form.name.trim(),
      registryId: form.registryId,
      country: form.country.trim().toUpperCase(),
      jurisdictionCode: form.jurisdictionCode,
      registryType: form.registryType,
      scrubCadenceDays: form.scrubCadenceDays,
      channel: (form.channel || null) as PREChannel | null,
      description: form.description.trim() || null,
      isActive: form.isActive,
    };

    if (isEdit && registry) {
      const success = await updateDncRegistry(registry.id, dto);
      if (success) onClose();
    } else {
      const id = await createDncRegistry(dto);
      if (id) onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title={isEdit ? `Edit Registry: ${registry?.registryId}` : 'Add DNC Registry'}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Name */}
        <div className="space-y-1.5">
          <Label htmlFor="dnc-name">Name *</Label>
          <Input
            id="dnc-name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. US National Do Not Call Registry"
          />
          {errors.name && (
            <p className="text-xs text-red-500">{errors.name}</p>
          )}
        </div>

        {/* Registry ID */}
        <div className="space-y-1.5">
          <Label htmlFor="dnc-id">Registry ID</Label>
          <Input
            id="dnc-id"
            value={form.registryId}
            readOnly={isEdit}
            onChange={(e) => !isEdit && setForm((p) => ({ ...p, registryId: e.target.value }))}
            className="font-mono text-sm bg-slate-50 dark:bg-slate-800"
          />
        </div>

        {/* Country + Jurisdiction */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="dnc-country">Country Code *</Label>
            <Input
              id="dnc-country"
              value={form.country}
              onChange={(e) => setForm((p) => ({ ...p, country: e.target.value.toUpperCase() }))}
              placeholder="US"
              maxLength={2}
            />
            {errors.country && (
              <p className="text-xs text-red-500">{errors.country}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Jurisdiction *</Label>
            <Select
              value={form.jurisdictionCode}
              onValueChange={(v) => setForm((p) => ({ ...p, jurisdictionCode: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select jurisdiction" />
              </SelectTrigger>
              <SelectContent>
                {jurisdictions.map((j) => (
                  <SelectItem key={j.code} value={j.code}>
                    {j.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.jurisdictionCode && (
              <p className="text-xs text-red-500">{errors.jurisdictionCode}</p>
            )}
          </div>
        </div>

        {/* Registry Type + Scrub Cadence */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Registry Type</Label>
            <Select
              value={form.registryType}
              onValueChange={(v) =>
                setForm((p) => ({ ...p, registryType: v as 'NATIONAL' | 'INTERNAL' }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REGISTRY_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dnc-cadence">Scrub Cadence (days) *</Label>
            <Input
              id="dnc-cadence"
              type="number"
              min={1}
              value={form.scrubCadenceDays}
              onChange={(e) =>
                setForm((p) => ({ ...p, scrubCadenceDays: Number(e.target.value) || 1 }))
              }
            />
            {errors.scrubCadenceDays && (
              <p className="text-xs text-red-500">{errors.scrubCadenceDays}</p>
            )}
          </div>
        </div>

        {/* Channel */}
        <div className="space-y-1.5">
          <Label>Channel Scope</Label>
          <Select
            value={form.channel}
            onValueChange={(v) =>
              setForm((p) => ({ ...p, channel: (v === '_all' ? '' : v) as PREChannel | '' }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              {CHANNEL_OPTIONS.map((opt) => (
                <SelectItem key={opt.value || '_all'} value={opt.value || '_all'}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="dnc-desc">Description</Label>
          <Input
            id="dnc-desc"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Optional description"
          />
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between py-2">
          <Label>Active</Label>
          <Switch
            checked={form.isActive}
            onCheckedChange={(checked) =>
              setForm((p) => ({ ...p, isActive: !!checked }))
            }
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isEdit ? 'Update Registry' : 'Create Registry'}
        </Button>
      </div>
    </Modal>
  );
}
